import { Suspense, type ReactNode } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import { adminPaths } from "@/app/router/paths";
import { RouteErrorPage } from "@/app/router/RouteErrorPage";
import { RouteLoadingState } from "@/app/router/RouteLoadingState";
import {
  AccessDeniedPage,
  ApplicationDetailPage,
  ApplicationsPage,
  AuditLogDetailPage,
  AuditLogsPage,
  BillingDetailPage,
  BillingPage,
  CourseDetailPage,
  CourseCreatePage,
  CoursesPage,
  DashboardPage,
  ReportsPage,
  AnnouncementsPage,
  OperationsPage,
  JobDetailPage,
  JobsPage,
  LoginPage,
  OrganizationDetailPage,
  OrganizationsPage,
  OwnershipTransfersPage,
  PrivacyPage,
  ReferenceDataPage,
  SupportCaseDetailPage,
  SupportInboxPage,
  UserDetailPage,
  UsersPage,
} from "@/app/router/lazyPages";
import { AdminLayout } from "@/layouts/AdminLayout";
import { AdminRouteGuard, PermissionGuard } from "@/modules/auth/components/AdminRouteGuard";

function lazy(element: ReactNode) {
  return <Suspense fallback={<RouteLoadingState />}>{element}</Suspense>;
}

function allowed(permission: string, element: ReactNode) {
  return <PermissionGuard permission={permission}>{lazy(element)}</PermissionGuard>;
}

export const router = createBrowserRouter([
  { path: "/", element: <Navigate to={adminPaths.dashboard} replace /> },
  { path: adminPaths.login, element: lazy(<LoginPage />), errorElement: <RouteErrorPage /> },
  { path: adminPaths.accessDenied, element: lazy(<AccessDeniedPage />), errorElement: <RouteErrorPage /> },
  {
    path: adminPaths.root,
    element: <AdminRouteGuard><AdminLayout /></AdminRouteGuard>,
    errorElement: <RouteErrorPage />,
    children: [
      { index: true, element: <Navigate to="dashboard" replace /> },
      { path: "dashboard", element: allowed("reports.view", <DashboardPage />) },
      { path: "reports", element: allowed("reports.view", <ReportsPage />) },
      { path: "announcements", element: allowed("notifications.broadcast", <AnnouncementsPage />) },
      { path: "operations", element: allowed("settings.manage", <OperationsPage />) },
      { path: "users", element: allowed("users.view", <UsersPage />) },
      { path: "users/:userId", element: allowed("users.view", <UserDetailPage />) },
      { path: "organizations", element: allowed("organizations.view", <OrganizationsPage />) },
      { path: "organizations/ownership-transfers", element: allowed("organizations.transfer", <OwnershipTransfersPage />) },
      { path: "organizations/:organizationId", element: allowed("organizations.view", <OrganizationDetailPage />) },
      { path: "jobs", element: allowed("jobs.view", <JobsPage />) },
      { path: "jobs/:jobId", element: allowed("jobs.view", <JobDetailPage />) },
      { path: "applications", element: allowed("applications.view", <ApplicationsPage />) },
      { path: "applications/:applicationId", element: allowed("applications.view", <ApplicationDetailPage />) },
      { path: "courses", element: allowed("courses.view", <CoursesPage />) },
      { path: "courses/new", element: allowed("courses.create", <CourseCreatePage />) },
      { path: "courses/:courseId", element: allowed("courses.view", <CourseDetailPage />) },
      { path: "reference-data", element: lazy(<ReferenceDataPage />) },
      { path: "billing", element: allowed("billing.view", <BillingPage />) },
      { path: "billing/invoices/:invoiceId", element: allowed("billing.view", <BillingDetailPage />) },
      { path: "billing/subscriptions/:subscriptionId", element: allowed("billing.view", <BillingDetailPage />) },
      { path: "privacy", element: allowed("privacy.view", <PrivacyPage />) },
      { path: "support", element: allowed("support.view", <SupportInboxPage />) },
      { path: "support/:supportId", element: allowed("support.view", <SupportCaseDetailPage />) },
      { path: "audit-logs", element: allowed("audit_logs.view", <AuditLogsPage />) },
      { path: "audit-logs/:auditId", element: allowed("audit_logs.view", <AuditLogDetailPage />) },
    ],
  },
  { path: "*", element: <Navigate to={adminPaths.dashboard} replace /> },
]);
